import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createContext } from "@/utils/context";
import { NAVBAR_BREAKPOINTS } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";

export type NavBarItem =
  | "subject-items"
  | "subjects-menu"
  | "trial-buttons-item"
  | "resources-menu"
  | "profile-license-abbrs"
  | "home-menu";

const visibleItemsByBreakpoint: NavBarItem[][] = [
  [
    "subject-items",
    "resources-menu",
    "profile-license-abbrs",
    "trial-buttons-item",
  ],
  [
    "subjects-menu",
    "resources-menu",
    "profile-license-abbrs",
    "trial-buttons-item",
  ],
  ["subjects-menu", "resources-menu", "trial-buttons-item"],
  ["home-menu"],
];

export type NavBarItemVisibilityContextValue = {
  isVisible: (item: NavBarItem) => boolean;
};

export const [NavBarItemVisibilityProvider, useNavBarItemVisibility] =
  createContext<NavBarItemVisibilityContextValue>({
    hookName: "useNavBarItemVisibility",
    providerName: "NavBarItemVisibilityProvider",
  });

const getScaledPx = (px: number) => Math.ceil(px);

export const useNavBarItemsVisibilityControl = () => {
  const [navBarEl, setNavBarEl] = useState<HTMLElement | null>(null);
  const [navBarLeftContainerEl, setNavBarLeftContainerEl] =
    useState<HTMLElement | null>(null);
  const [navBarRightContainerEl, setNavBarRightContainerEl] =
    useState<HTMLElement | null>(null);
  const [[breakpoint, isInitializing], setBreakpointState] = useState<
    [number, boolean]
  >([0, true]);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia(`(max-width: ${NAVBAR_BREAKPOINTS.sm}px)`).matches,
  );
  const navBarRightBreakpointWidths = useRef<Map<number, null | number>>(
    new Map([
      [0, null],
      [1, null],
      [2, null],
      [3, null],
    ]),
  );
  const checkBreakpoint = useCallback(() => {
    if (!navBarLeftContainerEl || !navBarRightContainerEl) return;
    const navBarLeftContainerRect =
      navBarLeftContainerEl.getBoundingClientRect();
    const navBarRightContainerRect =
      navBarRightContainerEl.getBoundingClientRect();
    const navBarLeftContainerEndX =
      navBarLeftContainerRect.x + navBarLeftContainerRect.width;
    setBreakpointState(([breakpoint, isInitializing]) => {
      if (
        getScaledPx(navBarLeftContainerEndX) >=
        getScaledPx(navBarRightContainerRect.x)
      ) {
        navBarRightBreakpointWidths.current.set(
          breakpoint,
          navBarRightContainerRect.width,
        );
        return [
          Math.min(breakpoint + 1, visibleItemsByBreakpoint.length - 1),
          isInitializing,
        ];
      }
      if (
        getScaledPx(
          navBarRightContainerRect.x +
            navBarRightContainerRect.width -
            navBarLeftContainerEndX,
        ) >
        getScaledPx(navBarRightBreakpointWidths.current.get(breakpoint - 1)!)
      ) {
        return [breakpoint - 1, isInitializing];
      }
      return [breakpoint, false];
    });
  }, [navBarLeftContainerEl, navBarRightContainerEl]);

  useLayoutEffect(() => {
    if (isInitializing) return;
    const observer = new ResizeObserver(() => {
      checkBreakpoint();
    });
    observer.observe(navBarRightContainerEl!);
    observer.observe(navBarLeftContainerEl!);
    observer.observe(navBarEl!);
    return () => {
      observer.disconnect();
    };
  }, [navBarEl, navBarRightContainerEl, checkBreakpoint, isInitializing]);

  useLayoutEffect(() => {
    if (!navBarEl || !navBarRightContainerEl || !isInitializing) return;
    checkBreakpoint();
  }, [
    navBarEl,
    navBarRightContainerEl,
    isInitializing,
    checkBreakpoint,
    breakpoint,
  ]);
  useLayoutEffect(() => {
    window
      .matchMedia(`(max-width: ${NAVBAR_BREAKPOINTS.sm}px)`)
      .addEventListener("change", (mql) => {
        setIsMobile(mql.matches);
      });
  }, []);

  return {
    navBarRef: (el: HTMLElement) => {
      setNavBarEl(el);
    },
    navBarLeftContainerRef: (el: HTMLElement | null) => {
      setNavBarLeftContainerEl(el);
    },
    navBarRightContainerRef: (el: HTMLElement | null) => {
      setNavBarRightContainerEl(el);
    },
    isInitializing,
    isVisible: useCallback(
      (item: NavBarItem) => {
        if (isMobile) return item === "home-menu";
        return visibleItemsByBreakpoint[breakpoint]!.includes(item);
      },
      [breakpoint, isMobile],
    ),
  };
};
